import React, { useState } from 'react'

import {
  BROWSER_SECTIONS,
  CHROME_EXTENSION_URL,
  DOWNLOAD_CENTER_URL,
  EDGE_EXTENSION_URL,
} from '../../constants'

import CHROME_LOGO from '@public/assets/chrome_logo.svg'
import EDGE_LOGO from '@public/assets/edge_logo.svg'
import ACCORDION_CHEVRON from '@public/assets/button/arrow-chevron-down.svg'
import EXTENSION_TUTORIAL_IMAGE from '@public/assets/tutorial/extension-tutorial.png'
import CHROME_TUTORIAL_VIDEO from '@public/assets/welcome-wizard/extension-tutorial-video-chrome.mp4'
import EDGE_TUTORIAL_VIDEO from '@public/assets/welcome-wizard/extension-tutorial-video-edge.mp4'
import { isTeamsApp } from '@/constants'
import { useWelcomeWizardVideo } from '../../hooks/useWelcomeWizardVideo'

const BrowserExtensionStep = () => {
  const [openSection, setOpenSection] = useState<number | null>(0)

  const userAgent = window.navigator.userAgent
  const isEdgeBrowser = userAgent.includes('Edg/')
  const isChromeBrowser = userAgent.includes('Chrome/') && !isEdgeBrowser
  const tutorialVideo = isEdgeBrowser
    ? EDGE_TUTORIAL_VIDEO
    : CHROME_TUTORIAL_VIDEO
  const videoRef = useWelcomeWizardVideo(tutorialVideo)

  const handleSectionToggle = (index: number) => {
    setOpenSection(currentSection => (currentSection === index ? null : index))
  }

  const renderStoreButton = ({
    disabled,
    href,
    icon,
    label,
  }: {
    disabled: boolean
    href: string
    icon: string
    label: string
  }) => {
    const className = `inline-flex h-10 items-center gap-2 rounded-full border border-[#d9d9d9] bg-white px-4 text-sm ${
      disabled
        ? 'cursor-not-allowed text-[#aaa] opacity-60'
        : 'text-[#333] hover:bg-neutral-100'
    }`
    const content = (
      <>
        <img alt="" className="h-6 w-6" src={icon} />
        <span>{label}</span>
        <span aria-hidden="true">↗</span>
      </>
    )

    if (disabled) return <span className={className}>{content}</span>

    return (
      <a className={className} href={href} rel="noreferrer" target="_blank">
        {content}
      </a>
    )
  }

  return (
    <div className="max-w-[900px]">
      <h2 className="text-lg font-semibold text-black">
        Capture Web-Based Activities
      </h2>
      <p className="mt-3 text-base text-[#666]">
        The MIRA Browser Extension helps identify work performed in websites and
        cloud applications.
      </p>
      <div className="mt-5">
        {BROWSER_SECTIONS.map(([title, descriptions], index) => {
          const isOpen = openSection === index

          return (
            <section key={title}>
              <button
                aria-expanded={isOpen}
                className="grid h-[39px] w-full grid-cols-[24px_1fr_20px] items-center gap-3 py-[10px] pl-[10px] text-left text-sm font-semibold text-[#333]"
                onClick={() => handleSectionToggle(index)}
                type="button"
              >
                <span>{index + 1}.</span>
                <span>{title}</span>
                <img
                  alt=""
                  className={`h-5 w-5 transition-transform ${
                    isOpen ? '-rotate-180' : ''
                  }`}
                  src={ACCORDION_CHEVRON}
                />
              </button>
              {isOpen && (
                <div className="border-b border-[#ddd] bg-[#f9f9f9] px-10 py-5 text-sm text-[#666]">
                  <ul className="list-disc pl-3">
                    {descriptions.map(description => (
                      <li key={description}>{description}</li>
                    ))}
                  </ul>
                  {index === 0 && (
                    <div className="mt-3 flex gap-2">
                      {isTeamsApp ? (
                        <a
                          className="inline-flex h-10 items-center gap-2 rounded-full bg-[#333] px-4 text-sm text-white hover:bg-black"
                          href={DOWNLOAD_CENTER_URL}
                          rel="noreferrer"
                          target="_blank"
                        >
                          <span aria-hidden="true">↗</span>
                          Download Center
                        </a>
                      ) : (
                        <>
                          {renderStoreButton({
                            disabled: isEdgeBrowser,
                            href: CHROME_EXTENSION_URL,
                            icon: CHROME_LOGO,
                            label: 'Chrome Extension',
                          })}
                          {renderStoreButton({
                            disabled: isChromeBrowser,
                            href: EDGE_EXTENSION_URL,
                            icon: EDGE_LOGO,
                            label: 'Edge Extension',
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          )
        })}
      </div>
      {isTeamsApp ? (
        <img
          alt="MIRA browser extension installation guide"
          className="mt-5 aspect-[900/518] w-full rounded-[20px] object-contain shadow-[0_1px_2px_rgba(0,0,0,0.3),0_2px_6px_2px_rgba(0,0,0,0.15)]"
          src={EXTENSION_TUTORIAL_IMAGE}
        />
      ) : (
        <video
          ref={videoRef}
          className="mt-5 aspect-[900/518] w-full rounded-[20px] bg-black object-contain shadow-[0_1px_2px_rgba(0,0,0,0.3),0_2px_6px_2px_rgba(0,0,0,0.15)]"
          controls
          autoPlay
          key={tutorialVideo}
          preload="metadata"
        >
          <source src={tutorialVideo} type="video/mp4" />
        </video>
      )}
    </div>
  )
}

export default BrowserExtensionStep
